import { isFunction, isObject } from "@/common/helpers/lang";
import type { Fn, OrFunction, OrPromise, Returned, UnknownArray } from "@/common/typings/lang";

/** Represents a successful result. */
export interface Ok<T> {
  success: true;
  data: T;
}

/** Represents an error result. */
export interface Err<T> {
  success: false;
  error: T;
}

/** Represents any result of an operation. */
export type Result<T, E> = Ok<T> | Err<E>;

/** Result type with unknown data and error. Use for type inference. */
export type UnknownResult = Result<unknown, unknown>;

/** Extracts all Ok objects from a provided type. */
export type ExtractOk<T> = Extract<T, Ok<unknown>>;

/** Extracts all Err objects from a provided type. */
export type ExtractErr<T> = Extract<T, Err<unknown>>;

/** Extracts all Ok objects from a provided type. Also extracts from promises and function results. */
export type InferOk<T> = ExtractOk<Awaited<Returned<T>>>;

/** Extracts all Err objects from a provided type. Also extracts from promises and function results. */
export type InferErr<T> = ExtractErr<Awaited<Returned<T>>>;

/** Extracts the data from an Ok object. Also extracts from promises and function results. */
export type UnwrapOk<T> = InferOk<T> extends never ? never : InferOk<T> extends Ok<infer R> ? R : never;

/** Extracts the error from an Err object. Also extracts from promises and function results. */
export type UnwrapErr<T> = InferErr<T> extends never ? never : InferErr<T> extends Err<infer R> ? R : never;

/** Creates an Ok object. */
export function ok<const T>(data: T): Ok<T> {
  return { success: true, data };
}

/** Creates an Err object. */
export function err<const T>(error: T): Err<T> {
  return { success: false, error };
}

/** Checks if a value is a Result. */
export function isResult(value: unknown): value is UnknownResult {
  return isObject(value) && "success" in value && ("data" in value || "error" in value);
}

/** Checks if a result is successful. */
export function isOk<T>(r: Result<T, unknown>): r is Ok<T> {
  return isResult(r) && r.success;
}

/** Checks if a result is an error. */
export function isErr<E>(r: Result<unknown, E>): r is Err<E> {
  return isResult(r) && !r.success;
}

/** Unwraps a result and throws if it's an error. */
export function unwrapOrThrow<T, E>(r: Result<T, E>): T {
  if (isOk(r)) {
    return r.data;
  }

  // eslint-disable-next-line @typescript-eslint/only-throw-error -- we don't control the error type here
  throw r.error;
}

/** Converts a Result-returning function to a throwable function. */
// prettier-ignore
export function toThrowable<A extends UnknownArray, R extends UnknownResult>(fn: Fn<A, R>): Fn<A, UnwrapOk<R>>;
// prettier-ignore
export function toThrowable<A extends UnknownArray, R extends UnknownResult>(
  fn: Fn<A, Promise<R>>,
): Fn<A, Promise<UnwrapOk<R>>>;
// prettier-ignore
export function toThrowable<A extends UnknownArray, R extends UnknownResult>(
  fn: Fn<A, OrPromise<R>>,
): Fn<A, OrPromise<UnwrapOk<R>>> {
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- function may not be async
  return (...args: A) => {
    const result = fn(...args);

    return result instanceof Promise
      ? (result.then(unwrapOrThrow) as Promise<UnwrapOk<R>>)
      : (unwrapOrThrow(result) as UnwrapOk<R>);
  };
}

/** Tries to execute a function and returns a Result. */
// prettier-ignore
export function tryCatch<T, const E>(
  toTry: OrFunction<Promise<T>>,
  toCatch: ToCatch<E>,
): Promise<Result<T, Awaited<E>>>;
// prettier-ignore
export function tryCatch<T, const E>(toTry: () => T, toCatch: ToCatch<Promise<E>>): Promise<Result<T, Awaited<E>>>;
// prettier-ignore
export function tryCatch<T, const E>(toTry: () => T, toCatch: ToCatch<E>): Result<T, E>;
// prettier-ignore
export function tryCatch<T, const E>(
  toTry: OrFunction<OrPromise<T>>,
  toCatch: ToCatch<E>,
): Result<T, E> | Promise<Result<T, E>> {
  const handleError = (error: E | Err<E>): Err<E> => (isObject(error) && isErr(error) ? error : err(error));

  const catchFn = (ex: unknown): OrPromise<Err<E>> => {
    const error = isFunction(toCatch) ? toCatch(ex) : toCatch;

    return error instanceof Promise ? error.then(handleError) : handleError(error);
  };

  try {
    const result = isFunction(toTry) ? toTry() : toTry;

    if (result instanceof Promise) {
      return result.then(ok).catch(catchFn);
    }

    return ok(result);
  } catch (ex) {
    return catchFn(ex);
  }
}

type ToCatch<E> = E | Err<E> | ((error: unknown) => E | Err<E>);
